import {
  Credenza,
  CredenzaBody,
  CredenzaTrigger,
  CredenzaContent,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
  CredenzaDescription,
} from '@/components/ui/credenza.tsx';
import { useMediaQuery } from '@/hooks/use-media-query.tsx';
import { useGetPlantUsersQuery } from '@/features/plants/hooks/queries/[plantId]/[user]/use-get-plant-users.ts';
import { usePostAssignUsersToPlant } from '@/features/plants/hooks/mutations/[plantId]/[user]/use-post-assign-users-to-plant.ts';
import { useGetUsersQuery } from '@/features/users/hooks/queries/get-all-users.tsx';
import { ReactNode, useEffect, useState } from 'react';
import { TPlant } from '@/features/plants/schemas/plants.schema.ts';
import { PlantUserAccessManagementDesktop } from './plant-user-access-management-desktop.tsx';
import { PlantUserAccessManagementMobile } from './plant-user-access-management-mobile.tsx';
import { Skeleton } from '@/components/ui/skeleton.tsx';
import { Button } from '@/components/ui/button.tsx';
import { TUser } from '@/features/users/schemas/users.schema.ts';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { ListRestart } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip.tsx';
import { useQueryClient } from '@tanstack/react-query';

export type TId = string | number;
export type TUserWithAccessControl = TUser & {
  columnId: TId;
  newlyAdded: boolean;
};

export const PlantUserAccessManagementDialog = ({
  plant,
  children,
}: {
  plant: TPlant;
  children: ReactNode;
}) => {
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery('(max-width:640px)');
  const [isUserManagementOpen, setIsUserManagementOpen] = useState(false);
  const [users, setUsers] = useState<TUserWithAccessControl[]>([]);

  const updatePlantUsersQueryMutation = usePostAssignUsersToPlant({
    queryClient,
    plantId: plant.plantId ?? '',
    onSuccess: () => {
      setIsUserManagementOpen(false);
    },
  });
  const { data: companyUsers, isLoading: isUsersLoading } = useGetUsersQuery({
    enabled: isUserManagementOpen,
  });
  const {
    data: assignedPlantUsers,
    isFetching,
    isLoading: isPlantUsersLoading,
  } = useGetPlantUsersQuery({
    plantId: plant.plantId ?? '',
    enabled: isUserManagementOpen,
  });
  const isLoading = isUsersLoading || isPlantUsersLoading;

  useEffect(() => {
    const initialUserList =
      companyUsers?.map((u) => {
        const columnId = assignedPlantUsers?.userIds?.includes(u.userId)
          ? 'associated-users'
          : 'available-users';

        return {
          ...u,
          columnId,
          newlyAdded: false,
        };
      }) ?? [];
    setUsers(initialUserList);
  }, [isLoading, isFetching]);

  useEffect(() => {
    // reset user list

    if (!isUserManagementOpen) {
      const initialUserList =
        companyUsers?.map((u) => {
          const columnId = assignedPlantUsers?.userIds?.includes(u.userId)
            ? 'associated-users'
            : 'available-users';

          return {
            ...u,
            columnId,
            newlyAdded: false,
          };
        }) ?? [];
      setUsers(initialUserList);
    }
  }, [isUserManagementOpen]);

  return (
    <Credenza open={isUserManagementOpen} onOpenChange={setIsUserManagementOpen}>
      <CredenzaTrigger asChild>{children}</CredenzaTrigger>

      <CredenzaContent className="max-w-4xl">
        <CredenzaHeader>
          <CredenzaTitle>{plant.basicDetails.name} User Access Management</CredenzaTitle>
        </CredenzaHeader>

        <CredenzaBody className="sm:h-[500px] sm:px-0">
          <CredenzaDescription>Assign and manage plant user permissions.</CredenzaDescription>
          {!isMobile ? (
            <div className="mt-4 h-full">
              <PlantUserAccessManagementDesktop
                users={users}
                setUsers={setUsers}
                isLoading={isLoading}
              />
            </div>
          ) : (
            <div className="mt-4 h-full">
              <PlantUserAccessManagementMobile
                users={users}
                setUsers={setUsers}
                isLoading={isLoading}
              />
            </div>
          )}
        </CredenzaBody>
        <CredenzaFooter className="sm:h-9">
          {isLoading && !isMobile && <Skeleton className="h-9 w-[62px]" />}
          {isLoading && isMobile && <Skeleton className="h-9"></Skeleton>}

          {!isLoading && (
            <div className="flex gap-2">
              {isMobile ? (
                <Button
                  variant="secondary"
                  disabled={updatePlantUsersQueryMutation.isPending}
                  onClick={() => {
                    const initialUserList =
                      companyUsers?.map((u) => {
                        const columnId = assignedPlantUsers?.userIds?.includes(u.userId)
                          ? 'associated-users'
                          : 'available-users';

                        return {
                          ...u,
                          columnId,
                          newlyAdded: false,
                        };
                      }) ?? [];
                    setUsers(initialUserList);
                  }}
                  className="p-1 px-1.5"
                >
                  <ListRestart className="h-6 w-6" />
                </Button>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="secondary"
                        disabled={updatePlantUsersQueryMutation.isPending}
                        onClick={() => {
                          const initialUserList =
                            companyUsers?.map((u) => {
                              const columnId = assignedPlantUsers?.userIds?.includes(u.userId)
                                ? 'associated-users'
                                : 'available-users';

                              return {
                                ...u,
                                columnId,
                                newlyAdded: false,
                              };
                            }) ?? [];
                          setUsers(initialUserList);
                        }}
                        className="p-1 px-1.5"
                      >
                        <ListRestart className="h-6 w-6" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="bg-slate-50 text-base text-black shadow-xs">
                      <p>Reset user access list</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              <Button
                disabled={updatePlantUsersQueryMutation.isPending}
                onClick={() => {
                  updatePlantUsersQueryMutation.mutate({
                    plantId: plant.plantId,
                    userIds: users
                      .filter((u) => u.columnId === 'associated-users')
                      .map((u) => u.userId),
                  });
                }}
                className="flex w-full min-w-20 justify-center gap-1 text-center"
              >
                {updatePlantUsersQueryMutation.isPending && <LoadingSpinner size={24} />}
                <span className="text-[16px] select-none">Save</span>
              </Button>
            </div>
          )}
        </CredenzaFooter>
      </CredenzaContent>
    </Credenza>
  );
};
